/**
 * 头奖名单每一行的取值。
 *
 * <p>这个板块的职责是**兑付记录** —— 大厅天天挂着「N 天没出头奖 · 奖池 R$ X」，
 * 而 `doc/BIG_WINS_BOARD_DESIGN.md` §5.6 记着那句话「在产品里一直是孤证」：玩家只
 * 看到一个越来越大的数字，没有任何东西证明这钱真的会付出去。这几行就是那个证明。
 *
 * <p><b>为什么不用 `shared/profile/avatarDefaults.ts` 的 `resolveAvatarUrl`。</b>
 * 它接受的直连字段是 `avatar_url / avatarUrl / avatar / avatar_thumb / avatarThumb`
 * —— **不含 `avatarThumbUrl`**，而 Milhar 的载荷用的正是这个名字。把 Milhar 的对象
 * 喂给它，自定义头像会被静默跳过、退回默认头像包，得到一个看起来完全正常的错头像。
 * 这里显式处理 Milhar 的字段名，与 `RoundDetailSheet` / `RoundFeed` 的既有取法一致。
 */

import { defaultAvatarUrl } from '../profile/avatarDefaults.ts';

/**
 * 这一行该显示哪个头像。
 *
 * @returns 图片地址；**返回 `''` 表示没有头像**，调用方应当渲染首字母块
 *   （`.milhar-feed-av.is-letter`），不要拿空串当 `src`
 */
export function winnerAvatarUrl(winner: {
  avatarThumbUrl?: string | null;
  avatarDefaultId?: number | null;
}): string {
  // trim 之后再判断：'   ' 是 truthy，直接当 src 会渲染成破图
  const direct = String(winner.avatarThumbUrl ?? '').trim();
  if (direct) return direct;
  // 必须自己复刻 defaultAvatarUrl 内部的合法区间判断（整数且 1..100），
  // 不能只看真值。**不能交给 defaultAvatarUrl 兜底** —— 它会把任何它会夹的值
  // （0、负数、>100、非整数……）都夹成 1，于是「这个人没有头像」变成
  // 「所有没头像 / 头像编号非法的人都是 1 号头像」。这里要拒绝的是
  // defaultAvatarUrl 会夹的整个类别，不是某一个具体的坏值。
  const raw = Number(winner.avatarDefaultId ?? 0);
  const id = Number.isInteger(raw) && raw >= 1 && raw <= 100 ? raw : 0;
  return id ? defaultAvatarUrl(id) : '';
}

/**
 * 这一行的期号文本。
 *
 * <p>`label` 是后端按开奖时区算好的**日内序号**，**自带 `#`**
 * （`MilharTier.roundLabel` 返回 `"#08"`）—— 所以这里不能再加一个。只有退回全局号
 * 时才自己补 `#`：那一行每个字段都是数字，没有 `#` 期号读起来就是其中一个数。
 */
export function winnerRoundText(winner: { label?: string | null; round: number }): string {
  const label = String(winner.label ?? '').trim();
  return label || `#${winner.round}`;
}
